import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchRecording,
  retranscribeRecording,
  RecordingMeta,
  RecordingTranscriptionSegment,
} from "lib/recordingsApi";
import {
  downloadTranscriptSrt,
  downloadTranscriptTxt,
  formatTranscriptClock,
} from "lib/transcriptExport";
import MeetingSummaryCard from "components/MeetingSummaryCard";
import { useTranslation } from "locales";
import "./style.scss";

interface Props {
  recordingId: string;
  /** Pre-fetched recording, if any. Panel will still refresh. */
  initialRecording?: RecordingMeta | null;
  /** Polling interval for pending/running jobs (ms). Default 3000. */
  pollIntervalMs?: number;
  className?: string;
}

type TranscriptionStatus = "pending" | "running" | "done" | "failed";
const TERMINAL_STATUSES: ReadonlyArray<TranscriptionStatus> = ["done", "failed"];

function colorForUser(userId: string | null): string {
  if (!userId) return "#8e8e93";
  // Simple deterministic hue from userId (golden-ratio hash).
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 55%, 42%)`;
}

function SegmentRow({ seg }: { seg: RecordingTranscriptionSegment }) {
  const name = seg.screenName || (seg.userId ? `User ${seg.userId}` : "Speaker");
  const color = colorForUser(seg.userId);
  return (
    <div className="rec-transcript__row">
      <span className="rec-transcript__row-who" style={{ color }}>
        {name}
      </span>
      <span className="rec-transcript__row-time">
        ({formatTranscriptClock(seg.start)})
      </span>
      <span className="rec-transcript__row-text">{seg.text}</span>
    </div>
  );
}

const RecordingTranscriptPanel: React.FC<Props> = ({
  recordingId,
  initialRecording,
  pollIntervalMs = 3000,
  className,
}) => {
  const { t } = useTranslation();
  const [recording, setRecording] = useState<RecordingMeta | null>(initialRecording || null);
  const [loading, setLoading] = useState(!initialRecording);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await fetchRecording(recordingId);
      if (!mountedRef.current) return;
      setRecording(next);
      setError(null);
      if (next && next.transcription && !TERMINAL_STATUSES.includes(next.transcription.status)) {
        timerRef.current = setTimeout(load, pollIntervalMs);
      }
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : "Failed to load recording");
      timerRef.current = setTimeout(load, pollIntervalMs * 2);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [recordingId, pollIntervalMs]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(!initialRecording);
    load();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId]);

  const handleRetry = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    setError(null);
    try {
      const updated = await retranscribeRecording(recordingId);
      setRecording(updated);
      stopPolling();
      timerRef.current = setTimeout(load, pollIntervalMs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Retry failed");
    } finally {
      if (mountedRef.current) setRetrying(false);
    }
  }, [recordingId, retrying, load, pollIntervalMs, stopPolling]);

  const status = recording?.transcription?.status || (recording ? "unknown" : "loading");
  const segments = recording?.transcription?.segments || [];
  const hasAudioTracks = (recording?.audioTracks?.length || 0) > 0;

  return (
    <div className={`rec-transcript${className ? " " + className : ""}`}>
      {status === "done" && segments.length > 0 && (
        <MeetingSummaryCard recordingId={recordingId} className="rec-transcript__coach" />
      )}

      <div className="rec-transcript__header">
        <span className="rec-transcript__title">{t("Transcript")}</span>
        <span className={`rec-transcript__status rec-transcript__status--${status}`}>
          {status === "pending" && t("Queued…")}
          {status === "running" && t("Transcribing…")}
          {status === "failed" && t("Failed")}
          {status === "done" && t("{{count}} segments", { count: segments.length })}
          {status === "loading" && t("Loading…")}
          {status === "unknown" && "—"}
        </span>
        <div className="rec-transcript__actions">
          {status === "done" && segments.length > 0 && (
            <>
              <button
                className="rec-transcript__btn"
                onClick={() => recording && downloadTranscriptTxt(recording)}
                type="button"
              >
                .txt
              </button>
              <button
                className="rec-transcript__btn"
                onClick={() => recording && downloadTranscriptSrt(recording)}
                type="button"
              >
                .srt
              </button>
            </>
          )}
          {hasAudioTracks && (status === "done" || status === "failed") && (
            <button
              className="rec-transcript__btn rec-transcript__btn--retry"
              onClick={handleRetry}
              disabled={retrying}
              type="button"
            >
              {retrying ? "…" : t("Retranscribe")}
            </button>
          )}
        </div>
      </div>

      {loading && <div className="rec-transcript__info">{t("Loading…")}</div>}

      {!loading && !hasAudioTracks && (
        <div className="rec-transcript__info">
          {t("This recording has no per-participant audio tracks and cannot be transcribed.")}
        </div>
      )}

      {error && <div className="rec-transcript__error">{error}</div>}

      {recording?.transcription?.status === "failed" && recording.transcription.error && (
        <div className="rec-transcript__error">{recording.transcription.error}</div>
      )}

      {status === "done" && segments.length === 0 && (
        <div className="rec-transcript__info">{t("No speech detected.")}</div>
      )}

      {segments.length > 0 && (
        <div className="rec-transcript__body">
          {segments.map((seg, i) => (
            <SegmentRow key={`${seg.userId || "x"}-${seg.start}-${i}`} seg={seg} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RecordingTranscriptPanel;
